import type { MediaComment, TikTokEmoji } from '../api/media-comments';

/**
 * Отбор комментариев в блок «Топовые комментарии под роликом».
 *
 * Отбор устроен в два слоя. Здесь — механика, которая не зависит от смысла
 * текста: что в отчёт не идёт никогда (ответы, реплики самого паблика, пустые),
 * как схлопываются одинаковые комментарии под разными копиями посева и в каком
 * порядке остальные выстраиваются.
 *
 * Оценка содержания — отдельным слоем, через `filter`. В отчёт нельзя ставить
 * комментарий с негативом, даже если он собрал больше всех лайков, а отличить
 * «Жарайсыңдар!» от издёвки по словарю нельзя — тем более на казахском. Пока
 * `filter` не передан, порядок чисто по лайкам, и оператор смотрит сам.
 */

export type SeedingComment = {
  id: string;
  /** Автор комментария без @. */
  username: string;
  text: string;
  likeCount: number;
  /** «19.09» — как подпись даты в приложении. */
  date: string;
  /** Под сколькими копиями посева встретился тот же текст. */
  copies: number;
  /** Комментарий из TikTok — и карточка у него тиктоковская. */
  platform?: 'tiktok';
  /** Имя профиля, которое TikTok показывает над комментарием вместо ника. */
  nickname?: string;
  /** Аватар на CDN TikTok; в карточку он попадает после перезаливки. */
  avatarUrl?: string | null;  /** Ответов под комментарием — строка «Просмотреть N ответов». */
  replyCount?: number;
  /** Фирменные эмодзи TikTok по позициям в text. */
  emojis?: TikTokEmoji[];
  /** Фото или стикер под текстом; рисуются после перезаливки, как аватар. */
  imageUrl?: string | null;
  stickerUrl?: string | null;
};

/**
 * Сюда встанет модель, которая отсеивает негатив. Получает уже очищенный и
 * отсортированный список, возвращает тот же список без неподходящих — порядок
 * менять можно, придумывать комментарии нельзя.
 */
export type CommentFilter = (comments: SeedingComment[]) => Promise<SeedingComment[]>;

export type PickCommentsOptions = {
  /** Сколько комментариев оставить в блоке. */
  count: number;
  filter?: CommentFilter;
};

/**
 * Казахские буквы к их русским двойникам: «Жарайсыңдар» и «жарайсындар» — это
 * один и тот же комментарий, набранный с казахской раскладкой и без неё, и в
 * блоке им делать нечего по отдельности. Свести буквы можно безбоязненно:
 * отпечаток только склеивает одинаковое, в отчёт идёт исходный текст.
 */
const KAZAKH_FOLD: Record<string, string> = {
  ә: 'а', ғ: 'г', қ: 'к', ң: 'н', ө: 'о', ұ: 'у', ү: 'у', һ: 'х', і: 'и',
};

/** Текст без регистра, эмодзи и повторов знаков — чтобы схлопнуть одинаковые. */
function fingerprint(text: string): string {
  return text
    .toLowerCase()
    .replace(/[әғқңөұүһі]/g, (ch) => KAZAKH_FOLD[ch])
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

function formatDate(timestamp: string | null, platform?: 'tiktok'): string {
  const ms = timestamp ? Date.parse(timestamp) : NaN;
  if (!Number.isFinite(ms)) return '';
  const date = new Date(ms);
  // TikTok подписывает дату «9-7» — месяц и день без нулей.
  if (platform === 'tiktok') return `${date.getMonth() + 1}-${date.getDate()}`;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}`;
}

/** Позиции эмодзи после trim(): текст в карточку идёт без пробелов по краям. */
function shiftEmojis(comment: MediaComment): TikTokEmoji[] | undefined {
  if (!comment.emojis) return undefined;
  const lead = comment.text.length - comment.text.trimStart().length;
  const length = comment.text.trim().length;
  return comment.emojis
    .map((e) => ({ ...e, start: e.start - lead, end: e.end - lead }))
    .filter((e) => e.start >= 0 && e.end <= length && e.start < e.end);
}

/**
 * Комментарии, которым в отчёте не место при любом содержании.
 *
 * Ответы — потому что вырванная из ветки реплика читается как реплика в пустоту.
 * Комментарии самого паблика — потому что отчёт о том, что написали люди, а не
 * о том, что ответила редакция. Пустые — это комментарии из одних эмодзи и
 * стикеров, от них в тексте остаётся пустая карточка.
 */
function isEligible(comment: MediaComment): boolean {
  return !comment.isReply && !comment.isOwner && fingerprint(comment.text).length > 0;
}

/**
 * Сырые комментарии всех копий посева → то, что встанет в отчёт.
 *
 * Одинаковые тексты под разными копиями — один комментарий: при посеве под
 * шестью публикациями набегают шесть «Жарайсыңдар!», и блок из них состоял бы
 * целиком. Остаётся самый залайканный, а сколько раз текст встретился, видно
 * в `copies` — это признак, что комментарий не случайный.
 */
export async function pickTopComments(
  comments: MediaComment[],
  options: PickCommentsOptions,
): Promise<SeedingComment[]> {
  if (options.count <= 0) return [];

  const byText = new Map<string, SeedingComment>();
  for (const comment of comments) {
    if (!isEligible(comment)) continue;
    const key = fingerprint(comment.text);
    const previous = byText.get(key);
    const copies = (previous?.copies ?? 0) + 1;
    // Из одинаковых оставляем тот, у которого лайков больше: это тот же текст,
    // но под копией, где его увидели.
    const best =
      previous && previous.likeCount >= comment.likeCount
        ? previous
        : {
            id: comment.id,
            username: comment.username,
            text: comment.text.trim(),
            likeCount: comment.likeCount,
            date: formatDate(comment.timestamp, comment.platform),
            ...(comment.platform
              ? {
                  platform: comment.platform,
                  nickname: comment.nickname,
                  avatarUrl: comment.avatarUrl,
                  replyCount: comment.replyCount,
                  emojis: shiftEmojis(comment),
                  imageUrl: comment.imageUrl,
                  stickerUrl: comment.stickerUrl,
                }
              : {}),
          };
    byText.set(key, { ...best, copies } as SeedingComment);
  }

  const ranked = [...byText.values()].sort(
    (a, b) => b.likeCount - a.likeCount || b.copies - a.copies || a.text.localeCompare(b.text, 'ru'),
  );

  const kept = options.filter ? await options.filter(ranked) : ranked;
  return kept.slice(0, options.count);
}
